import { useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import {
  GET_ADMIN_BY_ID,
  GetAdminByIdResponse,
} from '../../../apollo/queries/adminUsers/getAdminById.queries';
import { AdminToastHelper } from '../../../components/AdminUsers/AdminToast';
import { Organisation } from '../../../apollo/queries';
import { createDynamicApolloClient } from '../../../apollo/getApolloClient';
import { SHARED_TENANT } from '../../../utils/constants';
import {
  GET_USER_BY_ID,
  GetUserByIdResponse,
} from '../../../apollo/queries/adminUsers/getUserById.queries';

export type AdminOrg = Pick<Organisation, 'code' | 'name'>;

export interface AdminGeneralInfo {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  role: string;
  tenant: string;
  organisations: AdminOrg[];
}

export const initialAdminDetails: AdminGeneralInfo = {
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  confirmPassword: '',
  role: '',
  tenant: '',
  organisations: [],
};

const useGetAdminByIdState = (adminId: string, tenant: string) => {
  const [adminDetails, setAdminDetails] = useState<AdminGeneralInfo>({
    ...initialAdminDetails,
  });

  const getUpdatedDetails = (data: GetAdminByIdResponse) => {
    const {
      roles,
      tenant = '',
      memberships: mem,
      ...details
    } = data.adminUserById;

    const memberships = mem || [];
    const updatedTenantInputs = () => ({
      tenant,
      organisations: memberships.map((m) => ({
        code: m.organization,
        name: m.organization,
      })),
    });

    const tenantInputs =
      tenant !== '' ? updatedTenantInputs() : { tenant: '', organisations: [] };

    const updatedDetails: Omit<
      AdminGeneralInfo,
      'password' | 'confirmPassword'
    > = {
      ...details,
      ...tenantInputs,
      role: roles[0],
    };

    return updatedDetails;
  };

  const dynamicClient = useMemo(() => {
    if (tenant) return createDynamicApolloClient(tenant);
  }, [tenant]);

  const isSharedTenantAdmin = tenant === SHARED_TENANT || tenant === '';
  const onCompleteShared = (data: GetAdminByIdResponse) => {
    if (!data.adminUserById) return;
    setAdminDetails((prevDetails) => ({
      ...prevDetails,
      ...getUpdatedDetails(data),
    }));
  };

  const onCompleteTenantSpecific = (data: GetUserByIdResponse) => {
    if (!data.user) return;
    const sharedResponse: GetAdminByIdResponse = {
      adminUserById: {
        ...data.user,
        memberships: data.user.memberships?.map((m) => ({
          organization: m.organizationGlobal,
          roles: m.roles,
        })),
      },
    };
    onCompleteShared(sharedResponse);
  };
  const query = isSharedTenantAdmin ? GET_ADMIN_BY_ID : GET_USER_BY_ID;
  const onComplete = isSharedTenantAdmin
    ? onCompleteShared
    : onCompleteTenantSpecific;
  const { loading: isLoading, data } = useQuery<any>(query, {
    variables: { id: adminId },
    onCompleted: (data) => onComplete(data),
    onError: (e) => {
      AdminToastHelper.error();
    },
    client: isSharedTenantAdmin ? undefined : dynamicClient,
    fetchPolicy: 'cache-and-network',
  });

  return {
    isLoading,
    oldAdminDetails: data?.adminUserById || data?.user,
    adminDetails,
    setAdminDetails,
  };
};

export default useGetAdminByIdState;
